/*
  chemistry.js — water balance math for Pool Master.

  Pure functions, no app state: LSI (Langelier Saturation Index), ideal-range
  checks, and dosing recommendations sized to the pool's volume. Everything
  here is unit-tested in chemistry.test.js — if you change a constant, run
  `npm test`.

  Dose constants are the standard pool-industry amounts per 10,000 US gallons,
  cross-checked against the usual dealer charts. Metric output (g/kg, mL/L).
*/

const n = (v) => {
  const x = parseFloat(String(v ?? "").replace(/[^0-9.\-]/g, ""));
  return isFinite(x) ? x : null;
};

// "22,000" -> 22000. Returns null when unknown.
export function parseGallons(v) {
  const x = n(v);
  return x && x > 0 ? x : null;
}

/* ---------- targets (mid-points of the ideal ranges in WATER_PARAMS) ---------- */
export const TARGETS = {
  fc: { lo: 1, hi: 4, target: 3 },
  ph: { lo: 7.2, hi: 7.8, target: 7.5 },
  ta: { lo: 80, hi: 120, target: 100 },
  ch: { lo: 200, hi: 400, target: 300 },
  cya: { lo: 30, hi: 50, target: 40 },
  salt: { lo: 2700, hi: 3400, target: 3200 },
  phos: { hi: 300 },
  copper: { hi: 0.2 },
  iron: { hi: 0.2 },
};

/* ---------- LSI ---------- */
// Temperature factor (Taylor watergram table, °F), linear between points.
const TEMP_F = [[32, 0], [37, 0.1], [46, 0.2], [53, 0.3], [60, 0.4], [66, 0.5], [76, 0.6], [84, 0.7], [94, 0.8], [105, 0.9]];
export function tempFactor(tF) {
  const t = Math.max(32, Math.min(105, tF));
  for (let i = 1; i < TEMP_F.length; i++) {
    const [t0, f0] = TEMP_F[i - 1], [t1, f1] = TEMP_F[i];
    if (t <= t1) return f0 + ((t - t0) / (t1 - t0)) * (f1 - f0);
  }
  return 0.9;
}

const tdsFactor = (tds) => (tds <= 1000 ? 12.1 : tds <= 2500 ? 12.2 : tds <= 4500 ? 12.3 : 12.4);

/*
  lsi({ph, ta, ch, cya, salt, tempF}) -> { value, band, inputs } | null
  band: "corrosive" (< -0.30) | "balanced" | "scaling" (> +0.30)
  Carbonate alkalinity corrects TA for the cyanurate contribution (~CYA/3).
*/
export function lsi(r) {
  const ph = n(r.ph), ta = n(r.ta), ch = n(r.ch);
  if (ph == null || ta == null || ch == null || ta <= 0 || ch <= 0) return null;
  const cya = n(r.cya) || 0;
  const salt = n(r.salt) || 0;
  const tempF = n(r.tempF) ?? n(r.temp) ?? 80; // assume a warm pool when not measured
  const adjTA = Math.max(1, ta - cya / 3);
  const tds = salt > 0 ? salt + 800 : 1200;
  const value = ph + tempFactor(tempF) + (Math.log10(ch) - 0.4) + Math.log10(adjTA) - tdsFactor(tds);
  const rounded = Math.round(value * 100) / 100;
  const band = rounded < -0.3 ? "corrosive" : rounded > 0.3 ? "scaling" : "balanced";
  return { value: rounded, band, inputs: { ph, ta, adjTA: Math.round(adjTA), ch, cya, tempF, tds } };
}

export const LSI_LABEL = {
  corrosive: "Corrosive — etches surfaces & equipment",
  balanced: "Balanced",
  scaling: "Scale-forming — cloudiness & deposits",
};

/* ---------- dose formatting ---------- */
export function fmtDose(amount, unit) {
  // amount in g or mL; promote to kg / L at 1000.
  if (amount >= 995) {
    const big = amount / 1000;
    const v = big >= 10 ? Math.round(big) : Math.round(big * 10) / 10;
    return `${v} ${unit === "mL" ? "L" : "kg"}`;
  }
  const v = amount >= 100 ? Math.round(amount / 10) * 10 : Math.round(amount / 5) * 5;
  return `${Math.max(v, 5)} ${unit}`;
}

/* ---------- dosing ----------
  Per-1-unit doses per 10,000 US gallons (industry chart values):
    FC +1 ppm ......... 58 g cal-hypo 65%  |  0.32 L liquid chlorine 10.3%
    pH +0.1 ........... 85 g soda ash              (at TA ~100)
    pH -0.1 ........... 120 mL muriatic acid 31.45% (at TA ~100)
    TA +10 ppm ........ 680 g sodium bicarb
    TA -10 ppm ........ 780 mL muriatic acid 31.45%
    CH +10 ppm ........ 545 g calcium chloride 77%
    CYA +10 ppm ....... 370 g stabilizer
    Salt +100 ppm ..... 3.8 kg pool salt
*/
export function doseRecs(results, gallons, opts = {}) {
  const g = parseGallons(gallons) || 10000;
  const per10k = g / 10000;
  const saltPool = !!opts.saltPool;
  const recs = [];
  const r = (k) => n(results[k]);
  const push = (k, severity, title, dose, note) => recs.push({ k, severity, title, dose: dose || "", note: note || "" });

  const fc = r("fc"), tc = r("tc"), ph = r("ph"), ta = r("ta"), ch = r("ch"), cya = r("cya"), salt = r("salt");
  const cc = fc != null && tc != null ? Math.round((tc - fc) * 10) / 10 : null;

  // Combined chlorine first — shock covers a low FC too.
  let shocked = false;
  if (cc != null && cc >= 0.5) {
    shocked = true;
    const targetFC = Math.min(10 * cc, 12);
    const ppm = Math.max(targetFC - (fc || 0), 5);
    push("cc", "action", `Combined chlorine ${cc} ppm — shock the pool`,
      `${fmtDose(58 * ppm * per10k, "g")} cal-hypo 65% (or ${fmtDose(320 * ppm * per10k, "mL")} liquid chlorine)`,
      "Add in the evening with the pump running; retest before swimming.");
  }
  if (!shocked && fc != null && fc < TARGETS.fc.lo) {
    const ppm = TARGETS.fc.target - fc;
    push("fc", "action", `Free chlorine low (${fc} ppm)`,
      `${fmtDose(320 * ppm * per10k, "mL")} liquid chlorine 10.3% (or ${fmtDose(58 * ppm * per10k, "g")} cal-hypo 65%)`,
      saltPool ? "Salt pool: also check the cell output % and inspect for scale." : "");
  }
  if (fc != null && fc > TARGETS.fc.hi) {
    push("fc", fc > 10 ? "action" : "watch", `Free chlorine high (${fc} ppm)`,
      "No chlorine today",
      fc > 10 ? "Keep swimmers out until under 5 ppm; sunlight will bring it down." : "Hold dosing and retest in 24–48 h.");
  }

  // Alkalinity before pH — pH drifts until TA is right.
  const taOff = ta != null && (ta < TARGETS.ta.lo || ta > TARGETS.ta.hi);
  if (ta != null && ta < TARGETS.ta.lo) {
    const ppm = TARGETS.ta.target - ta;
    push("ta", "action", `Alkalinity low (${ta} ppm)`,
      `${fmtDose(680 * (ppm / 10) * per10k, "g")} sodium bicarb (Alkalinity)`,
      "Fix alkalinity first — pH steadies once TA is in range.");
  }
  if (ta != null && ta > TARGETS.ta.hi) {
    const ppm = ta - TARGETS.ta.target;
    push("ta", "action", `Alkalinity high (${ta} ppm)`,
      `${fmtDose(780 * (ppm / 10) * per10k, "mL")} muriatic acid 31.45%`,
      "Add slowly at the deep end, pump running — pH will drop too; retest both in 24 h.");
  }
  if (ph != null && ph < TARGETS.ph.lo) {
    const steps = (TARGETS.ph.target - ph) / 0.1;
    push("ph", "action", `pH low (${ph})`,
      `${fmtDose(85 * steps * per10k, "g")} soda ash (pH Plus)`,
      taOff ? "Alkalinity is also off — correct TA first, then retest pH." : "");
  }
  if (ph != null && ph > TARGETS.ph.hi) {
    const steps = (ph - TARGETS.ph.target) / 0.1;
    push("ph", "action", `pH high (${ph})`,
      `${fmtDose(120 * steps * per10k, "mL")} muriatic acid 31.45% (or ${fmtDose(95 * steps * per10k, "g")} pH Minus)`,
      taOff ? "Alkalinity is also off — correct TA first, then retest pH." : "");
  }

  if (ch != null && ch < TARGETS.ch.lo) {
    const ppm = TARGETS.ch.target - ch;
    push("ch", "action", `Calcium hardness low (${ch} ppm)`,
      `${fmtDose(545 * (ppm / 10) * per10k, "g")} calcium chloride 77%`,
      "Low calcium water pulls calcium out of plaster and grout.");
  }
  if (ch != null && ch > TARGETS.ch.hi) {
    const pct = Math.round((1 - TARGETS.ch.target / ch) * 100);
    push("ch", "watch", `Calcium hardness high (${ch} ppm)`,
      `Partial drain & refill ~${pct}%`, "No chemical lowers calcium — dilution only.");
  }
  if (cya != null && cya < TARGETS.cya.lo) {
    const ppm = TARGETS.cya.target - cya;
    push("cya", "action", `Stabilizer low (${cya} ppm)`,
      `${fmtDose(370 * (ppm / 10) * per10k, "g")} stabilizer (CYA)`,
      "Dissolves slowly — add via a sock in front of a return; don't backwash for 48 h.");
  }
  if (cya != null && cya > (saltPool ? 80 : TARGETS.cya.hi)) {
    const target = saltPool ? 70 : TARGETS.cya.target;
    const pct = Math.round((1 - target / cya) * 100);
    push("cya", "watch", `Stabilizer high (${cya} ppm)`,
      `Partial drain & refill ~${pct}%`, "High CYA locks up chlorine — dilution is the only fix.");
  }
  if (saltPool && salt != null && salt < TARGETS.salt.lo) {
    const ppm = TARGETS.salt.target - salt;
    push("salt", "action", `Salt low (${salt} ppm)`,
      `${fmtDose(3800 * (ppm / 100) * per10k, "g")} pool salt`,
      "Broadcast into the shallow end, brush to dissolve; generator off until dissolved.");
  }
  if (saltPool && salt != null && salt > TARGETS.salt.hi) {
    const pct = Math.round((1 - TARGETS.salt.target / salt) * 100);
    push("salt", "watch", `Salt high (${salt} ppm)`, `Partial drain & refill ~${pct}%`, "");
  }

  const phos = r("phos");
  if (phos != null && phos > TARGETS.phos.hi) {
    push("phos", "watch", `Phosphates high (${phos} ppb)`,
      `${fmtDose((phos > 1000 ? 1000 : 500) * per10k, "mL")} Phos-Free`,
      "Repeat weekly until under 300 ppb; expect clouding for a day — filter runs long.");
  }
  const copper = r("copper"), iron = r("iron");
  if ((copper != null && copper > TARGETS.copper.hi) || (iron != null && iron > TARGETS.iron.hi)) {
    push("metals", "watch", `Metals present (${copper != null && copper > 0.2 ? `Cu ${copper}` : ""}${iron != null && iron > 0.2 ? ` Fe ${iron}` : ""} ppm)`,
      `Metal sequestrant per label (~${fmtDose(300 * per10k, "mL")})`,
      "Do not shock until treated — metals stain when oxidized.");
  }

  return recs;
}

/* ---------- one-call analysis + report text ---------- */
export function chemAnalysis(results, gallons, opts = {}) {
  const idx = lsi(results || {});
  const recs = doseRecs(results || {}, gallons, opts);
  return { lsi: idx, recs, balanced: recs.length === 0 && !!idx && idx.band === "balanced" };
}

// Plain-text block used in emails / service reports.
export function chemSummaryText(results, gallons, opts = {}) {
  const { lsi: idx, recs } = chemAnalysis(results, gallons, opts);
  const lines = [];
  if (idx) lines.push(`Saturation index (LSI): ${idx.value >= 0 ? "+" : ""}${idx.value.toFixed(2)} — ${LSI_LABEL[idx.band]}`);
  if (!recs.length) {
    lines.push("Water is balanced — no chemicals needed today.");
  } else {
    lines.push("Recommended treatment" + (parseGallons(gallons) ? ` (for ${Number(parseGallons(gallons)).toLocaleString()} gal):` : ":"));
    recs.forEach((r) => lines.push(`  • ${r.title} — ${r.dose}${r.note ? `. ${r.note}` : ""}`));
  }
  return lines.join("\n");
}
